import { DOCUMENT, inject, isDevMode, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  GtagFn,
  IGoogleAnalyticsSettings,
  NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,
  NGX_GTAG_FN,
  provideGoogleAnalyticsRouter,
} from '@hakimio/ngx-google-analytics';

export const NGX_GOOGLE_ANALYTICS_INITIALIZER_PROVIDER = provideAppInitializer(() => {
  const initializerFn = GoogleAnalyticsInitializer(
    inject(NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN),
    inject(NGX_GTAG_FN),
    inject(DOCUMENT)
  );
  return initializerFn();
});

export function GoogleAnalyticsInitializer(settings: IGoogleAnalyticsSettings, gtag: GtagFn, document: Document) {
  return async () => {
    if (!settings.ga4TagId) {
      console.info('Empty tracking code for Google Analytics. Google analytics disabled.');
      return;
    }

    if (!gtag) {
      if (!isDevMode()) {
        console.error(
          "Couldn't create or read gtag() fn. Make sure this module is running on a Browser w/ access to Window interface."
        );
      }

      return;
    }

    if (!document) {
      if (!isDevMode()) {
        console.error(
          "Couldn't to access Document interface. Make sure this module is running on a Browser w/ access to Document interface."
        );
      }
    }

    settings.uri = settings.uri || `https://www.googletagmanager.com/gtag/js?id=${settings.ga4TagId}`;
    settings.initCommands = settings?.initCommands ?? [];

    if (!settings.initCommands.find((x) => x.command === 'config')) {
      settings.initCommands.unshift({ command: 'config', values: [settings.ga4TagId] });
    }

    if (!settings.initCommands.find((x) => x.command === 'js')) {
      settings.initCommands.unshift({ command: 'js', values: [new Date()] });
    }

    for (const command of settings.initCommands) {
      gtag(command.command, ...command.values);
    }

    const script: HTMLScriptElement = document.createElement('script');
    script.async = true;
    script.src = settings.uri;

    if (settings.nonce) {
      script.setAttribute('nonce', settings.nonce);
    }

    const head: HTMLHeadElement = document.getElementsByTagName('head')[0];
    head.appendChild(script);
  };
}

export function provideGoogleAnalytics(options: { keyFactory: () => string | undefined }) {
  const providers = [
    {
      provide: NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,
      useFactory: () => {
        const key = options.keyFactory();
        return {
          ga4TagId: key,
        } as IGoogleAnalyticsSettings;
      },
    },
    NGX_GOOGLE_ANALYTICS_INITIALIZER_PROVIDER,
    provideGoogleAnalyticsRouter(),
  ];

  return makeEnvironmentProviders(providers);
}
